import { SubscriptionStatus } from "constant";
import LocalStorageService from "service/localStorageService";
import {
    getAllTokens,
    getLatestSubscription,
    getSubscriptionInfo,
} from "service/subscriptionService";
import {
    changeOrganizationAction,
    storeOrganizationAction,
    storeOrgGroupsAction,
    storeOrgMembersAction,
    storeOrgRolesAction,
    storeTokenBalanceAction,
    updateIsUserOrgLoadedAction,
    updateSubscriptionStatus,
    verifyOrgOwnerAction,
} from "store/actions";
import { OrganisationDto } from "types/Organisation";
import { getSubscriptionPlanType } from "components/subscriptions/CurrentPlanInformation";
import { isHttpSuccess } from "./functions";
import { fetchOrgList } from "./organisationFunction";

export const prepareApp = async (
    storeDispatchActions: any,
    email: string,
    getOrgCallback: () => void | undefined = () => { }
): Promise<void> => {
    try {
        // Load user organizations and setup
        await getUserOrgs(storeDispatchActions, email, getOrgCallback);
        // Load subscription and token data, Json, timezone in parallel
        await Promise.all([
            getTokenBalance(storeDispatchActions),
            (async () => {
                const subscription = await getLatestSubscription();

                // This set local storage subscriptionPlanType logic is moved from Home to here
                // Dont use setLocalStorageSubscription hook here to avoid component mounted/unmounted issue
                // Using plain function to set local storage rather than React hook path.
                const { is_prepaid = null, is_cancellable = null } =
                    subscription?.customer ?? {};
                const subscriptionType = getSubscriptionPlanType({
                    is_prepaid,
                    is_cancellable,
                });

                if (!subscriptionType) {
                    return;
                }

                LocalStorageService.setItem(
                    "subscriptionPlanType",
                    subscriptionType
                );
            })(),
            getSubscriptionStatus(storeDispatchActions),
        ]);
    } catch (error) {
        console.error("Error during app preparation:", error);
        throw error;
    }
};

const getTokenBalance = async (storeDispatchActions: any): Promise<void> => {
    try {
        const total: any = await getAllTokens();
        storeDispatchActions(
            storeTokenBalanceAction({ tokenBalance: total, isLoaded: true })
        );
    } catch (error) {
        console.error("Error fetching token balance:", error);
        // Store with isLoaded: true but tokenBalance: 0 to indicate fetch attempt completed
        storeDispatchActions(
            storeTokenBalanceAction({ tokenBalance: 0, isLoaded: true })
        );
    }
};

export const getUserOrgs = async (
    storeDispatchActions: any,
    email: string,
    callback: () => void
): Promise<void> => {
    try {
        const orgList = await fetchOrgList();

        if (!orgList || orgList.length === 0) {
            throw new Error("No organisations found for user");
        }

        let orgId =
            LocalStorageService.getItem("lastLoggedOrgId") ||
            LocalStorageService.getItem("orgId");

        const currentOrg =
            orgList.find((org: OrganisationDto) => org.uuid === orgId) ||
            orgList.find((org: OrganisationDto) => org.isDefault);

        if (!currentOrg) {
            alert(
                "You do not have primary organisation, please contact Technical support."
            );
            throw new Error("No valid organisation found");
        }

        orgId = currentOrg.uuid;

        // Update local storage
        LocalStorageService.setItem("orgId", orgId);
        LocalStorageService.removeItem("lastLoggedOrgId");

        // Dispatch organization data
        storeDispatchActions(storeOrganizationAction({ orgList }));
        storeDispatchActions(
            changeOrganizationAction({
                orgInfo: currentOrg,
            })
        );
        storeDispatchActions(updateIsUserOrgLoadedAction({ isLoaded: true }));

        // Dispatch organization-related actions
        storeDispatchActions(
            verifyOrgOwnerAction({
                isOwner: email === currentOrg.owner,
            })
        );

        storeDispatchActions(
            storeOrgMembersAction({
                orgMembers: currentOrg.members,
            })
        );

        storeDispatchActions(
            storeOrgGroupsAction({
                orgGroups: currentOrg.groups,
            })
        );

        storeDispatchActions(
            storeOrgRolesAction({
                orgRoles: currentOrg.roles,
            })
        );

        // Execute callback after successful setup
        callback();
    } catch (error) {
        console.error("Error loading user organizations:", error);
        throw error;
    }
};

const getSubscriptionStatus = async (
    storeDispatchActions: any
): Promise<void> => {
    try {
        const subscriptionRes: any = await getSubscriptionInfo();

        if (!isHttpSuccess(subscriptionRes.status)) {
            return;
        }

        // Check if there's any active subscription
        const hasActiveSubscription = subscriptionRes.data?.some(
            (subscription: any) =>
                subscription.type_ === "SUBSCRIPTION" &&
                [
                    SubscriptionStatus.ACTIVE,
                    SubscriptionStatus.CANCELED,
                    SubscriptionStatus.PENDING,
                ].includes(subscription.status)
        );

        storeDispatchActions(
            updateSubscriptionStatus({
                isSubscribed: hasActiveSubscription,
                hasSubscribed: true,
            })
        );
    } catch (error) {
        console.error("Error fetching subscription status:", error);
    }
};
